import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import React from "react";
import useSWR, { SWRConfiguration, SWRResponse } from "swr";
import { useStoredSessions } from "./useStoredSessions";

// Source code taken from: https://github.com/vercel/swr/blob/335f425a913532df96d95a8e63ef62f89cb05961/examples/axios-typescript/libs/useRequest.ts

export type GetRequest = AxiosRequestConfig;

export type PostRequest = AxiosRequestConfig & {
  method: "POST";
};

export interface Return<Data, Error>
  extends Pick<SWRResponse<AxiosResponse<Data>, AxiosError<Error>>, "isValidating" | "mutate" | "error" | "isLoading"> {
  data: Data | undefined;
  response: AxiosResponse<Data> | undefined;
}

export function useRequest<Data = unknown, Error = unknown>(
  initialRequest: GetRequest,
  config: SWRConfiguration<Data, Error> = {},
): Return<Data, Error> {
  const { getValues, update } = useStoredSessions();
  const { sessionId } = getValues();
  // Extract and build out session headers for every request.
  const headers = (initialRequest as AxiosRequestConfig).headers ?? {};
  if (sessionId && initialRequest.url) {
    // This could be null on the first call, but should be filled afterwards.
    headers["X-Session-ID"] = sessionId;
  }
  if (initialRequest.url) {
    headers["Content-type"] = "application/json";
    initialRequest.withCredentials = true;
  }
  Object.keys(headers).length > 0 && (initialRequest.headers = headers);
  const swrRequestKey = Object.keys(initialRequest).length !== 0 ? JSON.stringify(initialRequest) : null;
  const fetcher = () => axios(initialRequest);

  const { data: response, error, isValidating, mutate, isLoading } = useSWR(swrRequestKey, fetcher as any, config);

  React.useEffect(() => {
    // TODO: This hook executed by browser 19 times in the PDP page loading state and many more afterwards. Find a way to optimise this hook. We use React.useCallback but might not be enough
    !isValidating && response?.headers && update(response?.headers as Record<string, string>);
  }, [response, isValidating]);

  return {
    data: response && response.data,
    response,
    error,
    isValidating,
    isLoading,
    mutate,
  };
}
